/**
 * Definições (em linguagem simples) dos indicadores do Dashboard de Produtos.
 * Fiel ao cálculo de `fv-api/report/product_dashboard.go` e `product_dashboard_calc.go`.
 *
 * Campos: `title` (nome exibido), `what` (o que mede), `how` (como é calculado),
 * `read` (como interpretar).
 *
 * Conceitos usados em vários textos:
 * - "venda" = item de venda Finalizada ou item de produto de Ordem de Serviço Paga;
 * - "catálogo" = produtos ativos e não genéricos (genérico não controla estoque);
 * - "custo" = custo de compra cadastrado do produto vendido;
 * - o período anterior tem a mesma duração do período escolhido;
 * - valores de estoque em datas passadas são reconstruídos pelas movimentações, usando
 *   custo e preço atuais (por isso aparecem como estimativa).
 */
export const PRODUCT_INDICATOR_HELP = {
    attention: {
        title: 'Precisa de atenção agora',
        what: 'Um resumo rápido dos problemas de estoque e preço que merecem ação imediata.',
        how: 'Mostra a posição de hoje, sem depender do período escolhido: produtos em ruptura (saldo zerado), com estoque baixo, abaixo da margem esperada e o capital parado em produtos sem venda. Cada bloco leva à tela relacionada, se você tiver permissão.',
        read: 'Comece por aqui: ruptura é venda perdida, margem baixa é lucro perdido e capital parado é dinheiro sem giro. Detalhes de cada um estão nos indicadores abaixo.',
    },
    revenue: {
        title: 'Receita de produtos',
        what: 'Quanto a empresa faturou vendendo produtos no período.',
        how: 'Soma do valor dos produtos vendidos em vendas finalizadas e em ordens de serviço pagas. Serviços não entram, apenas produtos. A barra mostra quanto da receita foi para o custo e quanto sobrou de lucro; também mostra unidades vendidas, preço médio por unidade e receita média por dia.',
        read: 'Maior é melhor. Compare com o período anterior (seta ao lado do valor) para saber se as vendas de produtos estão crescendo ou caindo.',
    },
    net: {
        title: 'Lucro líquido',
        what: 'O que sobrou da venda de produtos depois de descontar o custo dos próprios produtos.',
        how: 'Receita de produtos − custo dos produtos vendidos. Não desconta despesas da empresa (aluguel, salários, impostos etc.), por isso é parecido com o "lucro bruto" da contabilidade. O percentual é a margem: lucro ÷ receita.',
        read: 'Maior é melhor. Receita subindo com lucro estável ou caindo indica que você está vendendo mais, mas ganhando menos por venda.',
    },
    margin: {
        title: 'Margem média',
        what: 'De cada R$ 100 vendidos em produtos, quantos reais ficam como lucro depois do custo.',
        how: 'Lucro líquido ÷ receita de produtos no período, em porcentagem. A margem esperada média é a média das margens configuradas nas categorias.',
        read: 'Maior é melhor. Se estiver abaixo da margem esperada, há produtos sendo vendidos barato demais ou com custo subindo. Veja "Abaixo da margem esperada".',
    },
    units: {
        title: 'Unidades vendidas',
        what: 'Quantas unidades de produtos foram vendidas no período.',
        how: 'Soma das quantidades dos itens de produto em vendas finalizadas e ordens de serviço pagas. O preço médio por unidade é a receita de produtos ÷ unidades vendidas.',
        read: 'Mostra o volume, independente do preço. Se a receita sobe mas as unidades caem, o aumento veio de preço maior ou de produtos mais caros.',
    },
    cost: {
        title: 'Custo dos produtos vendidos',
        what: 'Quanto custou comprar os produtos que foram vendidos no período.',
        how: 'Soma, para cada unidade vendida, do custo cadastrado do produto. O percentual é custo ÷ receita de produtos.',
        read: 'Aqui, subir é pior: se o custo cresce mais rápido que a receita, a margem diminui. Revise preços de venda ou negocie com fornecedores.',
    },
    stockValue: {
        title: 'Valor em estoque (custo)',
        what: 'Quanto dinheiro está parado em mercadoria no estoque, ao preço que você pagou por ela.',
        how: 'Soma de saldo × custo de cada produto na data final do período. "A preço de venda" usa o preço de venda atual; o "potencial" é a diferença entre os dois, ou seja, o ganho se tudo fosse vendido. Em datas passadas, o saldo é reconstruído pelas movimentações, com custo e preço atuais (estimativa).',
        read: 'Não é bom nem ruim por si: estoque alto demais prende dinheiro, estoque de menos causa ruptura. Avalie junto com giro e cobertura.',
    },
    turnover: {
        title: 'Giro de estoque',
        what: 'Quantas vezes o estoque "se renovou" (foi vendido e reposto) no período.',
        how: 'Custo dos produtos vendidos ÷ valor médio do estoque (a custo) no período. Exemplo: custo vendido de R$ 20 mil com estoque médio de R$ 10 mil dá giro 2,0. O estoque médio é reconstruído pelas movimentações, com custo atual (estimativa). Fica vazio se não há estoque.',
        read: 'Maior é melhor: o dinheiro investido volta mais rápido. Giro baixo indica estoque encalhado. O número depende do tamanho do período escolhido, então compare períodos de mesma duração.',
    },
    coverage: {
        title: 'Cobertura de estoque',
        what: 'Por quantos dias o estoque atual duraria mantendo o ritmo de vendas do período.',
        how: 'Valor médio do estoque × dias do período ÷ custo dos produtos vendidos. Fica vazio se não houve vendas no período. É uma estimativa, baseada em estoque reconstruído com custo atual.',
        read: 'Muito alto significa estoque demais (capital parado); muito baixo, risco de ruptura. O ideal depende do prazo de reposição dos seus fornecedores.',
    },
    activeProducts: {
        title: 'Produtos ativos',
        what: 'Quantos produtos do catálogo estão cadastrados e disponíveis para venda.',
        how: 'Conta os produtos ativos que controlam estoque (produtos genéricos não entram). É a posição atual, sem histórico. Serve de base para as porcentagens de outros indicadores.',
        read: 'Apenas informativo. Catálogo grande demais com poucas vendas pode indicar produtos parados que valem ser revistos.',
    },
    lowStock: {
        title: 'Estoque baixo',
        what: 'Produtos que ainda têm saldo, mas já estão abaixo do estoque mínimo configurado.',
        how: 'Conta produtos com saldo maior que zero e menor que o estoque mínimo cadastrado, na data final do período. Produtos sem mínimo configurado não entram. A porcentagem é sobre o catálogo.',
        read: 'Menor é melhor. É o aviso para repor antes de faltar. Configure o estoque mínimo dos produtos para que este aviso funcione.',
    },
    zeroStock: {
        title: 'Produtos em ruptura',
        what: 'Produtos sem nenhuma unidade disponível.',
        how: 'Conta os produtos com saldo zero (ou negativo) na data final do período.',
        read: 'Menor é melhor. Cada produto em ruptura é venda perdida enquanto não houver reposição; priorize os que mais vendem.',
    },
    noSale: {
        title: 'Sem venda no período',
        what: 'Produtos do catálogo que não venderam nenhuma unidade no período.',
        how: 'Conta produtos ativos, já cadastrados no fim do período, sem nenhuma venda (finalizada ou em OS paga). A porcentagem é sobre o catálogo.',
        read: 'Menor é melhor. Mesmo sem saldo, produto que não vende pode ser retirado do catálogo; com saldo, vira capital parado.',
    },
    parked: {
        title: 'Capital parado',
        what: 'Dinheiro investido em mercadoria que não vendeu nada no período.',
        how: 'Para cada produto sem venda no período e com saldo, multiplica o saldo pelo custo e soma tudo.',
        read: 'Menor é melhor. É dinheiro que poderia estar no caixa. Considere promoção, kit ou devolução ao fornecedor para liberá-lo.',
    },
    belowMargin: {
        title: 'Abaixo da margem esperada',
        what: 'Produtos cuja margem de lucro atual está menor que a margem configurada para a categoria.',
        how: 'Compara a margem do preço de venda atual (preço e custo cadastrados hoje) com a margem esperada da categoria (a subcategoria prevalece, quando houver). É a posição de hoje, sem relação com o período. A porcentagem é sobre o catálogo.',
        read: 'Menor é melhor. Cada produto aqui está sendo vendido por menos do que deveria; veja a lista e o preço sugerido.',
    },
    potentialGain: {
        title: 'Ganho potencial com preços sugeridos',
        what: 'Quanto a mais a empresa teria ganhado no período se vendesse pelo preço sugerido.',
        how: 'Para cada produto abaixo da margem: (preço sugerido − preço atual) × unidades vendidas no período, somados. Considera só produtos em que o sugerido é maior que o atual.',
        read: 'Maior indica mais dinheiro "deixado na mesa". É uma estimativa: assume que o volume de vendas se manteria com o preço novo.',
    },
    losing: {
        title: 'Vendendo com prejuízo',
        what: 'Produtos que foram vendidos por menos do que custaram.',
        how: 'Conta produtos vendidos no período cuja receita total ficou abaixo do custo total (margem negativa).',
        read: 'Deve ser zero. Cada venda desses produtos gera perda; corrija preço ou custo primeiro.',
    },
    promotions: {
        title: 'Produtos em promoção',
        what: 'Quantos produtos têm uma promoção vigente.',
        how: 'Conta os produtos com promoção ativa hoje. É a posição atual, sem histórico.',
        read: 'Apenas informativo. Útil para entender se a queda de margem tem relação com promoções.',
    },
    revenueCostChart: {
        title: 'Receita x custo',
        what: 'Compara, em cada dia, semana ou mês, o que você vendeu em produtos com o que esses produtos custaram.',
        how: 'Para cada barra, soma a receita e o custo dos produtos vendidos naquele intervalo (a escala muda conforme o tamanho do período). Ao passar o mouse aparece o lucro líquido = receita − custo.',
        read: 'Quanto maior a distância entre a barra de receita e a de custo, maior o lucro. Barras próximas indicam margem apertada.',
    },
    marginChart: {
        title: 'Margem média ao longo do tempo',
        what: 'Como a margem de lucro dos produtos variou durante o período.',
        how: 'Em cada ponto: (receita − custo) ÷ receita dos produtos vendidos naquele intervalo. A linha tracejada é a margem esperada média das categorias. Intervalos sem vendas não têm ponto.',
        read: 'Linha acima da tracejada é bom. Quedas bruscas podem indicar descontos, promoções ou aumento de custo.',
    },
    topSellers: {
        title: 'Produtos mais vendidos',
        what: 'Os 5 produtos que mais saíram, em quantidade, no período.',
        how: 'Ranking pelo número de unidades vendidas (em caso de empate, vence a maior receita). Abaixo, a receita e a margem de cada produto.',
        read: 'São seus campeões de saída. Garanta que não faltem em estoque e confira se a margem deles é saudável.',
    },
    abc: {
        title: 'Curva ABC de produtos',
        what: 'Separa os produtos pela importância na receita (princípio de Pareto: poucos produtos geram a maior parte do faturamento).',
        how: 'Os produtos vendidos são ordenados da maior para a menor receita e somados. Classe A: os que juntos formam os primeiros 80% da receita; classe B: até 95%; classe C: o restante. O gráfico mostra o % acumulado da receita (vertical) pelo % de produtos (horizontal).',
        read: 'A = carro-chefe: proteja estoque e preço. B = intermediários: mantenha cobertura. C = cauda longa: avalie se vale continuar vendendo.',
    },
    categories: {
        title: 'Receita e margem por categoria',
        what: 'Quanto cada categoria de produtos vende e quanto rende de lucro.',
        how: 'Soma a receita dos produtos por categoria (as 5 maiores aparecem; as demais são agrupadas em "Outros"). A porcentagem ao lado do valor é a fatia da categoria na receita total; a etiqueta é a margem da categoria (lucro ÷ receita), em amarelo quando abaixo da margem esperada média.',
        read: 'Mostra onde está o dinheiro e onde ele rende mais. Categoria com muita receita e margem baixa merece revisão de preço.',
    },
    belowMarginList: {
        title: 'Lista: abaixo da margem esperada',
        what: 'Os produtos com as piores margens em relação ao que foi configurado.',
        how: 'Mostra até 5 produtos, da menor margem para a maior, com preço atual, preço sugerido, margem atual e esperada. O botão "Aplicar" (para quem tem permissão) atualiza o preço de venda para o sugerido, recalculado no momento do clique.',
        read: 'Comece pelos primeiros da lista. Confira se o preço sugerido é viável para o mercado antes de aplicar.',
    },
    lowStockList: {
        title: 'Lista: estoque baixo e ruptura',
        what: 'Os produtos que precisam de reposição.',
        how: 'Mostra até 5 produtos: primeiro os zerados (os que mais venderam no período na frente) e depois os de estoque baixo (menor cobertura primeiro). A cobertura é o saldo ÷ venda média diária do período; fica "—" se o produto não vendeu.',
        read: 'Reponha de cima para baixo. Cobertura de poucos dias significa que o produto acaba logo.',
    },
    noSaleList: {
        title: 'Lista: sem venda no período',
        what: 'Os produtos parados que mais prendem dinheiro.',
        how: 'Mostra até 5 produtos com saldo e sem venda no período, dos maiores valores parados (saldo × custo) para os menores, com a data da última venda.',
        read: 'Bons candidatos para promoção, kit ou devolução ao fornecedor, liberando capital.',
    },
};

export const PRODUCT_GLOSSARY_ORDER = [
    'attention',
    'revenue', 'net', 'margin', 'units', 'cost',
    'stockValue', 'turnover', 'coverage', 'activeProducts', 'lowStock', 'zeroStock', 'noSale', 'parked',
    'belowMargin', 'potentialGain', 'losing', 'promotions',
    'revenueCostChart', 'marginChart', 'topSellers', 'abc', 'categories',
    'belowMarginList', 'lowStockList', 'noSaleList',
];
